import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords match
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    // Validate password length
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setIsSuccess(true);
      toast.success('Password reset successfully!');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to reset password';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1737629918399-45526c4f9264?auto=format&fit=crop&w=1920&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" />
      
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/cf-logo.png" alt="CreatorFlow" className="w-12 h-12 rounded-xl" />
            <h1 className="text-3xl font-bold tracking-tight">
              <span className="gradient-text">CreatorFlow</span>
            </h1>
          </div>
        </div>

        <Card className="bg-slate-900/90 border-slate-800 backdrop-blur-xl">
          {/* No token provided */}
          {!token && (
            <>
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2 text-red-400">
                  <AlertCircle className="w-5 h-5" />
                  Invalid Link
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-300">
                  This password reset link is invalid or has expired.
                </p>
                <Link to="/forgot-password">
                  <Button className="w-full btn-gradient" data-testid="request-new-link">
                    Request New Reset Link
                  </Button>
                </Link>
              </CardContent>
            </>
          )}

          {/* Success state */}
          {token && isSuccess && (
            <>
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2 text-green-400">
                  <CheckCircle className="w-5 h-5" />
                  Password Reset Complete
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-300">
                  Your password has been successfully reset. You will be redirected to the login page shortly.
                </p>
                <Link to="/">
                  <Button className="w-full btn-gradient" data-testid="go-to-login">
                    Go to Login Now
                  </Button>
                </Link>
              </CardContent>
            </>
          )}

          {/* Error state */}
          {token && !isSuccess && error && (
            <>
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2 text-red-400">
                  <AlertCircle className="w-5 h-5" />
                  Reset Failed
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-300">{error}</p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1 border-slate-700 hover:bg-slate-800"
                    onClick={() => setError(null)}
                    data-testid="try-again"
                  >
                    Try Again
                  </Button>
                  <Link to="/forgot-password" className="flex-1">
                    <Button className="w-full btn-gradient" data-testid="request-new-link">
                      New Reset Link
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </>
          )}

          {/* Reset form */}
          {token && !isSuccess && !error && (
            <>
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <Lock className="w-5 h-5 text-violet-400" />
                  Set New Password
                </CardTitle>
                <CardDescription>
                  Enter your new password below. Make sure it's at least 8 characters.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <Input
                      id="new-password"
                      data-testid="reset-new-password"
                      type="password"
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="bg-slate-950 border-slate-700"
                      required
                      minLength={8}
                      autoFocus
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                    <Input
                      id="confirm-password"
                      data-testid="reset-confirm-password"
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="bg-slate-950 border-slate-700"
                      required
                      minLength={8}
                    />
                  </div>
                  {newPassword && confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-red-400 text-sm">Passwords do not match</p>
                  )}
                  <Button 
                    type="submit" 
                    className="w-full btn-gradient"
                    data-testid="reset-password-submit"
                    disabled={isLoading || newPassword !== confirmPassword || newPassword.length < 8}
                  >
                    {isLoading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </form>
              </CardContent>
            </>
          )}
        </Card>

        <div className="text-center mt-4">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
            data-testid="back-to-login"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
